<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col-12">
        <h1 class="h2 mb-1 fw-bold">Chemical Usage Reports</h1>
        <p class="text-muted">View chemical usage by organization with detailed drill-downs</p>
      </div>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>

    <!-- Filters Card -->
    <div class="card mb-4">
      <div class="card-body">
        <!-- Row 1: Report View + Display Unit -->
        <div class="row mb-3 align-items-center">
          <div class="col-md-8">
            <label class="form-label me-3 small text-body-secondary">Report View</label>
            <div class="btn-group" role="group">
              <button
                type="button"
                class="btn btn-sm"
                [ngClass]="reportView === 'by-organization' ? 'btn-primary' : 'btn-outline-primary'"
                (click)="reportView = 'by-organization'; $any($event.target).blur()"
              >By Organization</button>
              <button
                type="button"
                class="btn btn-sm"
                [ngClass]="reportView === 'by-chemical' ? 'btn-primary' : 'btn-outline-primary'"
                (click)="reportView = 'by-chemical'; $any($event.target).blur()"
              >By Chemical</button>
            </div>
          </div>
          <div class="col-md-4 d-flex align-items-center justify-content-md-end gap-2 mt-3 mt-md-0">
            <label class="form-label mb-0 small text-muted">Display in</label>
            <select class="form-select form-select-sm w-auto" [(ngModel)]="unitUsed">
              <option value="original">Original units</option>
              @for (unit of volumeUnits; track $index) {
                <option [value]="unit.value">{{ unit.shortLabel }}</option>
              }
            </select>
          </div>
        </div>

        <!-- Row 2: Date Range, Organization, Manufacturer, Generate -->
        <div class="row g-3 align-items-end">
          <!-- Date Range -->
          <div class="col-md-2">
            <label class="form-label small text-body-secondary">Start Date</label>
            <input
              type="date"
              class="form-control"
              [(ngModel)]="filterStartDate"
            />
          </div>

          <div class="col-md-2">
            <label class="form-label small text-body-secondary">End Date</label>
            <input
              type="date"
              class="form-control"
              [(ngModel)]="filterEndDate"
            />
          </div>

          <!-- Organization Filter -->
          <div class="col-md-3">
            <label class="form-label small text-body-secondary">Organization</label>
            <select
              class="form-select"
              [(ngModel)]="filterOrganization"
            >
              <option value="">All Organizations</option>
              <option *ngFor="let org of organizations" [value]="org.id">{{ org.name }}</option>
            </select>
          </div>

          <!-- Chemical Filter (specific org only) -->
          <div class="col-md-3" *ngIf="filterOrganization">
            <label class="form-label small text-body-secondary">Chemical (Optional)</label>
            <select
              class="form-select"
              [(ngModel)]="filterChemical"
            >
              <option value="">All Chemicals</option>
              <option *ngFor="let chem of chemicals" [value]="chem.id">
                {{ chem.name }} {{ chem.epaNumber ? '(' + chem.epaNumber + ')' : '' }}
              </option>
            </select>
          </div>

          <!-- Manufacturer Filter (all orgs only) -->
          <div class="col-md-3" *ngIf="!filterOrganization">
            <label class="form-label small text-body-secondary">Manufacturer</label>
            <select
              class="form-select"
              [(ngModel)]="filterManufacturer"
            >
              <option value="">All Manufacturers</option>
              <option *ngFor="let mfr of manufacturers" [value]="mfr">{{ mfr }}</option>
            </select>
          </div>

          <!-- Generate Report Button -->
          <div class="col-md-2">
            <button
              type="button"
              class="btn btn-primary w-100"
              (click)="generateReport()"
              [disabled]="loading || !filterStartDate || !filterEndDate"
            >
              <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
              <i *ngIf="!loading" class="bi bi-search me-2"></i>
              Generate
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-muted mt-2">Loading report data...</p>
    </div>

    <!-- Organization Detail View -->
    <div *ngIf="!loading && orgDetailData">
      <!-- Export Button -->
      <div class="d-flex justify-content-end mb-3">
        <button
          type="button"
          class="btn btn-primary"
          (click)="exportToPdf()"
          [disabled]="exportLoading"
        >
          <span *ngIf="exportLoading" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!exportLoading" class="bi bi-file-earmark-pdf me-2"></i>
          Export PDF
        </button>
      </div>

      <!-- Summary Card -->
      <div class="card mb-4">
        <div class="card-body py-4">
          <h3 class="text-muted mb-3 text-center">{{ orgDetailData.organizationName }}</h3>
          <p class="text-muted small text-center">
            {{ formatDate(orgDetailData.startDate) }} - {{ formatDate(orgDetailData.endDate) }}
          </p>

          <!-- Chemical Usage Table -->
          <div class="table-responsive">
            <table class="table data-table mb-0">
              <thead>
                <tr>
                  <th>Chemical Name</th>
                  <th>EPA Number</th>
                  <th class="text-end">Total Amount Used{{ unitUsed !== 'original' ? ' (' + unitUsed + ')' : '' }}</th>
                  <th class="text-end">Flight Count</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngIf="orgDetailData.chemicalUsage.length === 0">
                  <td colspan="4" class="text-center text-muted py-3">
                    No chemical usage data for selected period
                  </td>
                </tr>
                <tr *ngFor="let chemical of orgDetailData.chemicalUsage">
                  <td>{{ chemical.chemicalName }}</td>
                  <td>{{ chemical.epaNumber || '-' }}</td>
                  <td class="text-end">{{ chemical.totalAmountUsed | chemAmount: chemical.unit : unitUsed }}</td>
                  <td class="text-end">{{ chemical.flightCount }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Drill-Down Sections -->
      <div class="row g-4">
        <!-- By Field -->
        <div class="col-lg-12">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="bi bi-map me-2"></i>
                By Field
              </h6>
            </div>
            <div class="table-responsive">
              <table class="table data-table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Field Name</th>
                    <th>Chemical Name</th>
                    <th>EPA Number</th>
                    <th class="text-end">Amount Used{{ unitUsed !== 'original' ? ' (' + unitUsed + ')' : '' }}</th>
                    <th class="text-end">Flight Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngIf="orgDetailData.byField.length === 0">
                    <td colspan="5" class="text-center text-muted py-3">
                      No data for selected period
                    </td>
                  </tr>
                  <ng-container *ngFor="let field of orgDetailData.byField">
                    <tr *ngFor="let chemical of field.chemicals; let first = first">
                      <td *ngIf="first" [attr.rowspan]="field.chemicals.length">{{ field.name }}</td>
                      <td>{{ chemical.chemicalName }}</td>
                      <td>{{ chemical.epaNumber || '-' }}</td>
                      <td class="text-end">{{ chemical.totalAmountUsed | chemAmount: chemical.unit : unitUsed }}</td>
                      <td class="text-end">{{ chemical.flightCount }}</td>
                    </tr>
                  </ng-container>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- By Pilot -->
        <div class="col-lg-12">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="bi bi-person-badge me-2"></i>
                By Pilot
              </h6>
            </div>
            <div class="table-responsive">
              <table class="table data-table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Pilot Name</th>
                    <th>Chemical Name</th>
                    <th>EPA Number</th>
                    <th class="text-end">Amount Used{{ unitUsed !== 'original' ? ' (' + unitUsed + ')' : '' }}</th>
                    <th class="text-end">Flight Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngIf="orgDetailData.byPilot.length === 0">
                    <td colspan="5" class="text-center text-muted py-3">
                      No data for selected period
                    </td>
                  </tr>
                  <ng-container *ngFor="let pilot of orgDetailData.byPilot">
                    <tr *ngFor="let chemical of pilot.chemicals; let first = first">
                      <td *ngIf="first" [attr.rowspan]="pilot.chemicals.length">{{ pilot.name }}</td>
                      <td>{{ chemical.chemicalName }}</td>
                      <td>{{ chemical.epaNumber || '-' }}</td>
                      <td class="text-end">{{ chemical.totalAmountUsed | chemAmount: chemical.unit : unitUsed }}</td>
                      <td class="text-end">{{ chemical.flightCount }}</td>
                    </tr>
                  </ng-container>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- By Drone -->
        <div class="col-lg-12">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="bi bi-robot me-2"></i>
                By Drone
              </h6>
            </div>
            <div class="table-responsive">
              <table class="table data-table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Drone Name</th>
                    <th>Chemical Name</th>
                    <th>EPA Number</th>
                    <th class="text-end">Amount Used{{ unitUsed !== 'original' ? ' (' + unitUsed + ')' : '' }}</th>
                    <th class="text-end">Flight Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngIf="orgDetailData.byDrone.length === 0">
                    <td colspan="5" class="text-center text-muted py-3">
                      No data for selected period
                    </td>
                  </tr>
                  <ng-container *ngFor="let drone of orgDetailData.byDrone">
                    <tr *ngFor="let chemical of drone.chemicals; let first = first">
                      <td *ngIf="first" [attr.rowspan]="drone.chemicals.length">{{ drone.name }}</td>
                      <td>{{ chemical.chemicalName }}</td>
                      <td>{{ chemical.epaNumber || '-' }}</td>
                      <td class="text-end">{{ chemical.totalAmountUsed | chemAmount: chemical.unit : unitUsed }}</td>
                      <td class="text-end">{{ chemical.flightCount }}</td>
                    </tr>
                  </ng-container>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- All Organizations Summary View -->
    <div *ngIf="!loading && summaryData">
      <!-- Export Button -->
      <div class="d-flex justify-content-end mb-3">
        <button
          type="button"
          class="btn btn-primary"
          (click)="exportToPdf()"
          [disabled]="exportLoading"
        >
          <span *ngIf="exportLoading" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!exportLoading" class="bi bi-file-earmark-pdf me-2"></i>
          Export PDF
        </button>
      </div>

      <div class="card overflow-hidden">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="bi bi-building me-2"></i>
            All Organizations - {{ formatDate(summaryData.startDate) }} to {{ formatDate(summaryData.endDate) }}
            <span *ngIf="summaryData.manufacturer" class="text-muted"> (Manufacturer: {{ summaryData.manufacturer }})</span>
          </h6>
        </div>
        <div class="table-responsive">
          <table class="table data-table table-hover mb-0">
            <thead>
              <tr>
                <th>Organization Name</th>
                <th>Chemical Name</th>
                <th>EPA Number</th>
                <th class="text-end">Amount Used{{ unitUsed !== 'original' ? ' (' + unitUsed + ')' : '' }}</th>
                <th class="text-end">Flight Count</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngIf="summaryData.organizations.length === 0">
                <td colspan="5" class="text-center text-muted py-4">
                  No data for selected period
                </td>
              </tr>
              <ng-container *ngFor="let org of summaryData.organizations">
                <tr *ngFor="let chemical of org.chemicals; let first = first">
                  <td *ngIf="first" [attr.rowspan]="org.chemicals.length">{{ org.organizationName }}</td>
                  <td>{{ chemical.chemicalName }}</td>
                  <td>{{ chemical.epaNumber || '-' }}</td>
                  <td class="text-end">{{ chemical.totalAmountUsed | chemAmount: chemical.unit : unitUsed }}</td>
                  <td class="text-end">{{ chemical.flightCount }}</td>
                </tr>
              </ng-container>
            </tbody>
            <tfoot *ngIf="summaryData.organizations.length > 0">
              <tr class="fw-bold">
                <td colspan="4">Grand Total Flights</td>
                <td class="text-end">{{ summaryData.grandTotalFlights }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Chemical Aggregate View (By Chemical) -->
    <div *ngIf="!loading && aggregateData">
      <!-- Export Button -->
      <div class="d-flex justify-content-end mb-3">
        <button
          type="button"
          class="btn btn-primary"
          (click)="exportToPdf()"
          [disabled]="exportLoading"
        >
          <span *ngIf="exportLoading" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!exportLoading" class="bi bi-file-earmark-pdf me-2"></i>
          Export PDF
        </button>
      </div>

      <div class="card overflow-hidden">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="bi bi-flask me-2"></i>
            All Chemicals - {{ formatDate(aggregateData.startDate) }} to {{ formatDate(aggregateData.endDate) }}
            <span *ngIf="aggregateData.manufacturer" class="text-muted"> (Manufacturer: {{ aggregateData.manufacturer }})</span>
          </h6>
        </div>
        <div class="table-responsive">
          <table class="table data-table table-hover mb-0">
            <thead>
              <tr>
                <th>Chemical Name</th>
                <th>Manufacturer</th>
                <th>EPA Number</th>
                <th class="text-end">Total Amount Used{{ unitUsed !== 'original' ? ' (' + unitUsed + ')' : '' }}</th>
                <th class="text-end">Organizations</th>
                <th class="text-end">Flight Count</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngIf="aggregateData.chemicals.length === 0">
                <td colspan="6" class="text-center text-muted py-4">
                  No chemical usage data for selected period
                </td>
              </tr>
              <tr *ngFor="let chem of aggregateData.chemicals">
                <td>{{ chem.chemicalName }}</td>
                <td>{{ chem.manufacturer || '-' }}</td>
                <td>{{ chem.epaNumber || '-' }}</td>
                <td class="text-end">{{ chem.totalAmountUsed | chemAmount: chem.unit : unitUsed }}</td>
                <td class="text-end">{{ chem.organizationCount }}</td>
                <td class="text-end">{{ chem.flightCount }}</td>
              </tr>
            </tbody>
            <tfoot *ngIf="aggregateData.chemicals.length > 0">
              <tr class="fw-bold">
                <td colspan="5">Grand Total Flights</td>
                <td class="text-end">{{ aggregateData.grandTotalFlights }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- No Data State -->
    <div *ngIf="!loading && !errorMessage && !orgDetailData && !summaryData && !aggregateData" class="text-center py-5">
      <i class="bi bi-bar-chart display-1 text-muted"></i>
      <p class="text-muted mt-3">Select your filters and click Generate to view the report.</p>
    </div>
  </div>
</div>
